/**
 * Registry helpers used by both the client and the Worker.
 */

import type { LanguageCode, LanguageOption } from "../types/language.js";
import { AUTO_LANGUAGE, listLanguages, resolveLanguage } from "./registry.js";

export * from "./registry.js";
export type { LanguageCode, LanguageOption } from "../types/language.js";

const normalize = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const SEARCH_INDEX = new Map<LanguageCode, string>();

for (const language of listLanguages()) {
  const parts = [language.code, language.name, language.nativeName, language.region ?? "", language.note ?? "", ...(language.aliases ?? [])];
  SEARCH_INDEX.set(language.code, normalize(parts.filter(Boolean).join(" ")));
}

/** Type-ahead over code, English name, endonym, region and aliases. */
export function searchLanguages(query: string, limit = 40): LanguageOption[] {
  const needle = normalize(query);
  const options = listLanguages().map((language) => {
    const haystack = SEARCH_INDEX.get(language.code) ?? "";
    const code = normalize(language.code);
    let rank = 3;
    if (!needle) rank = 0;
    else if (code === needle) rank = 0;
    else if (code.startsWith(needle)) rank = 1;
    else if (matchesWord(haystack, needle)) rank = 1;
    else if (haystack.includes(needle)) rank = 2;
    return { language, rank };
  });

  return options
    .filter((entry) => entry.rank < 3)
    .sort((a, b) => a.rank - b.rank || a.language.name.localeCompare(b.language.name))
    .slice(0, limit)
    .map(({ language }) => ({
      code: language.code,
      label: language.region ? `${language.name} · ${language.region}` : language.name,
      nativeLabel: language.nativeName,
      search: SEARCH_INDEX.get(language.code) ?? "",
    }));
}

/**
 * Word-boundary prefix match.
 *
 * This is what makes "frances" find "français" and "mandarin" find
 * "Mandarin Chinese" — a user typing an unaccented approximation still gets the
 * language they meant.
 */
function matchesWord(haystack: string, needle: string): boolean {
  if (needle === "") return false;
  let from = 0;
  for (;;) {
    const index = haystack.indexOf(needle, from);
    if (index === -1) return false;
    const before = index === 0 ? " " : haystack[index - 1] ?? " ";
    const after = haystack[index + needle.length] ?? " ";
    if (!/[a-z0-9]/.test(before) && !/[a-z0-9]/.test(after)) return true;
    from = index + 1;
  }
}

/** Picker options including the "Auto-detect" entry when requested. */
export function languageOptions(includeAuto = false): LanguageOption[] {
  const options = searchLanguages("", Number.MAX_SAFE_INTEGER);
  if (!includeAuto) return options;
  return [
    {
      code: AUTO_LANGUAGE,
      label: "Detect language",
      nativeLabel: "Auto",
      search: "auto detect automatic autodetect",
    },
    ...options,
  ];
}

/**
 * Coerce arbitrary user/detector input into a canonical registry code.
 * Returns `null` when the language is genuinely unknown.
 */
export function coerceLanguageCode(input: string | null | undefined): LanguageCode | null {
  if (!input) return null;
  const language = resolveLanguage(input);
  return language ? language.code : null;
}
